import Config from 'lib/Config';
import { orZero } from 'lib/utils/NumUtils';
import { CampaignDataFilter } from 'lib/data/models/CampaignObjects';
import BarGraph from 'components/shared/graphs/BarGraph';
import { shortFormatCurrency } from 'lib/utils/currency';
import { createIsoTimeRange, DAYS, now, daysInThisMonth, thisMonthSoFarLength } from 'utils/DateUtils.js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import MetricService from 'services/data/MetricService';
import Button from '../../shared/app/Button';
import EditGoals from './components/EditGoals';
import { adspendData, adspendOptions } from './utils/adspend';
import { aovData, aovOptions } from './utils/aov';
import { costPerPurchaseData, costPerPurchaseOptions } from './utils/costPerPurchase';
import { cppMessages, messages } from './utils/messages';
import { revenueData, revenueOptions } from './utils/revenue';
import { roasData, roasOptions } from './utils/roas';
import { getAccountProviders, getInnerError, renderError, renderLoader, getGoal } from 'utils/adaya/AdayaUtils';
import { getLogger } from 'lib/utils/logging';
import { ErrorMessages } from 'lib/AppErrors';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import { PageHelper } from 'lib/helpers/PageHelper';
import { ProviderNames } from 'lib/data/models/Providers';
import { Goal } from 'lib/data/models/Goals';
import { calculateMetricsAcrossDays } from 'utils/adaya/ProviderUtils';
import { findSuggestedMax } from 'components/shared/graphs/utils';
import './style.scss';
import GoalHelper from 'lib/helpers/GoalHelper';

const { log, warn } = getLogger('GoalTracker', { enabled: false });

// Orders = entire cart orders
// Revenue = total sales
// Profit = Revenue - adspend
// MER = Shopify Store Revenue / Adspend
// ROAS = Adaya tracked Revenue / Adspend
// CPP = Adspend / Orders
// AOV = Revenue / Orders


const GOAL_METRICS = [
    'currentMonth', 'daysInMonth',
    'monthProjectedRevenue', 'monthProjectedAdspend', 'monthProjectedRoas', 'monthProjectedAov', 'monthProjectedCpp',
];

const pageHelper = new PageHelper();

const Goals = (props) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);

    const [tab, setTab] = useState(undefined);
    const [previousTab, setPreviousTab] = useState('');
    const [dataReady, setDataReady] = useState(false);
    const [editingRevenueGoal, setRevenueGoal] = useState(0);
    const [editingCppGoal, setCppGoal] = useState(0);
    const [editingAovGoal, setAovGoal] = useState(0);
    const [editingMerGoal, setMerGoal] = useState(0);
    const [editingRoasGoal, setRoasGoal] = useState(0);

    const { auth } = props;

    const user = auth?.user;
    const account = user?.activeAccount;

    const [processedData, setProcessedData] = useState(undefined);
    const [processedROASData, setProcessedROASData] = useState(undefined);
    const [currentMonthData, setCurrentMonthData] = useState(undefined);
    const [monthProjectedRevenue, setMonthProjectedRevenue] = useState(0);
    const [monthProjectedAdspend, setMonthProjectedAdspend] = useState(0);
    const [monthProjectedMer, setMonthProjectedMer] = useState(0);
    const [monthProjectedAov, setMonthProjectedAov] = useState(0);
    const [monthProjectedCpp, setMonthProjectedCpp] = useState(0);
    const [monthRevenueSuggestedMax, setMonthRevenueSuggestedMax] = useState(0);
    const [monthAdspendSuggestedMax, setMonthAdspendSuggestedMax] = useState(0);
    const [monthMerSuggestedMax, setMonthMerSuggestedMax] = useState(0);
    const [monthCppSuggestedMax, setMonthCppSuggestedMax] = useState(0);
    const [monthAovSuggestedMax, setMonthAovSuggestedMax] = useState(0);

    const location = useLocation();
    const navigate = useNavigate();

    const {
        currentMonth
    } = MetricService.getSet(GOAL_METRICS);

    // todo: get goal from GoalHelper
    const daysInMonth = daysInThisMonth();
    const revenueGoal = getGoal(account, Goal.Revenue);
    const merGoal = getGoal(account, Goal.Mer);
    const roasGoal = getGoal(account, Goal.Roas);
    const aovGoal = getGoal(account, Goal.Aov);
    const cppGoal = getGoal(account, Goal.Cpp);
    const adspendGoal = getGoal(account, Goal.Adspend);

    useEffect(async () => {
        await fetchAllData();
    }, [user]);

    useEffect(() => {
        if (location.pathname == '/goals/edit') {
            setTab('edit-goals');
            setPreviousTab('revenue');
        } else if (location.pathname == '/goals') {
            setTab('revenue');
            setPreviousTab(tab);
        }
    }, [location]);

    // data is changed, ready
    useEffect(() => {
        if (processedData && currentMonthData) {
            const _thisMonthSoFarLength = thisMonthSoFarLength();
            setMonthProjectedRevenue(orZero((currentMonthData.revenue / _thisMonthSoFarLength) * daysInMonth))
            setMonthProjectedAdspend(orZero((currentMonthData.adspend / _thisMonthSoFarLength) * daysInMonth))
            setMonthProjectedMer(currentMonthData.mer)
            setMonthProjectedCpp(currentMonthData.cpp)
            setMonthProjectedAov(currentMonthData.aov)

            setMonthRevenueSuggestedMax(findSuggestedMax(currentMonthData.revenueAvg, Math.max(...currentMonthData.revenueData), revenueGoal / daysInMonth, .1))
            setMonthAdspendSuggestedMax(findSuggestedMax(currentMonthData.adspendAvg, Math.max(...currentMonthData.adspendData), adspendGoal / daysInMonth, .2))
            setMonthMerSuggestedMax(findSuggestedMax(currentMonthData.merAvg, Math.max(...currentMonthData.merData), merGoal / daysInMonth, .1))
            setMonthCppSuggestedMax(findSuggestedMax(currentMonthData.cppAvg, Math.max(...currentMonthData.cppData), cppGoal / daysInMonth, .1))
            setMonthAovSuggestedMax(findSuggestedMax(currentMonthData.aovAvg, Math.max(...currentMonthData.aovData), aovGoal / daysInMonth, .1))

            setDataReady(true);
        }
    }, [processedData, currentMonthData]);

    // fetch for all date ranges in parallel
    const fetchAllData = async () => {
        log(`fetchAllData`, account);
        if (!account) return;
        setLoading(true);

        try {
            await Promise.all([
                fetchData('last30days', (r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    console.log(`adaya (mer) data, last 30: `, _processedData)
                    setProcessedData(_processedData);
                }, [ProviderNames.ADAYA]), // omit ADAYA (mer = shopify revenue/adspend)

                fetchData('last30days', (r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    console.log(`shopify (roas) data, last 30: `, _processedData)
                    setProcessedROASData(_processedData);
                }, [ProviderNames.SHOPIFY]), // omit SHOPIFY (roas = adaya revenue/adspend)

                fetchData('thisMonth', (r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    setCurrentMonthData(_processedData);
                })
            ]);
        } catch (e) {
            log(`Exception fetching data`, e)
            setError(getInnerError(e));
        }

        setLoading(false);
    };

    // fetch for all date ranges in parallel
    const fetchData = async (range, cb, omitProviders?) => {
        try {
            const filter = getFilterFromSelection(range, omitProviders);
            // get both in parallel
            let r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, cb);
        } catch (e) {
            throw e;
        }
    }

    const onDataResponse = (r: ManagedRequest, cb) => {
        try {
            if (!r) throw "Response was empty.";

            if (r.status == RequestStatus.FINISHED) {
                cb(r)
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    // Goal needs all campaign data except Adaya
    const getFilterFromSelection = (range, omitProviders): CampaignDataFilter => {
        let timeRange = createIsoTimeRange(range);
        if (!timeRange)`A timeRange could not be created from the current selection: ${range}`;
        if (!account) throw `No account found for the current user.`;
        const providers = getAccountProviders(account, omitProviders || [ProviderNames.ADAYA]).map(p => p.name);
        return {
            account,
            timeRange,
            providers
        };
    }

    const onTabClick = (_tab) => {
        if (_tab != tab) {
            setPreviousTab(tab);
            setTab(_tab);
        }
    };

    const onCloseGoals = () => {
        setTab(previousTab || 'revenue');
        setPreviousTab('edit-goals');
        navigate('/goals');
    };

    const renderTab = (tabName, tabId, target, actual, projected, options) => {
        let msg = tabName + ' ';
        let msgs = tabId == 'cpp' ? cppMessages : messages;
        let dayIdx = moment().format('D') + 1;
        let percentageGoal = parseFloat(((projected - target) / projected) * 100).toFixed(2);

        if (percentageGoal < -33) {
            let idx = dayIdx % msgs.wayDown.length;
            msg += msgs.wayDown[idx];
        } else if (percentageGoal >= -33 && percentageGoal < -10) {
            let idx = dayIdx % msgs.down.length;
            msg += msgs.down[idx];
        } else if (percentageGoal >= -10 && percentageGoal < 0) {
            let idx = dayIdx % msgs.slightlyDown.length;
            msg += msgs.slightlyDown[idx];
        } else if (percentageGoal < 10 && percentageGoal >= 0) {
            let idx = dayIdx % msgs.slightlyUp.length;
            msg += msgs.slightlyUp[idx];
        } else if (percentageGoal >= 10 && percentageGoal < 33) {
            let idx = dayIdx % msgs.up.length;
            msg += msgs.up[idx];
        } else if (percentageGoal >= 33) {
            let idx = dayIdx % msgs.wayUp.length;
            msg += msgs.wayUp[idx];
        } else {
            msg += msgs.recalculating;
        }

        let statusGood = (options && options.swapped) ? 'status-bad' : 'status-good';
        let statusBad = (options && options.swapped) ? 'status-good' : 'status-bad';

        return (
            <div className={'tab tab-' + tabId + (tab == tabId ? ' active' : '') + ' ' + (percentageGoal >= 0 ? statusGood : statusBad)}
                onClick={() => onTabClick(tabId)}>
                <div className="label">{msg}</div>
            </div>
        );
    };

    // Todo: migrate to new data system/backend.
    const updateGoal = async (name, value) => {
        try {
            if (!account) return;

            if (!account.metadata) {
                account.metadata = {
                    goals: GoalHelper.makeNewGoals()
                };
            }

            account.metadata.goals[name] = value;

            // update cpp from other goals:
            const revenueGoal = getGoal(account, 'revenue');
            const merGoal = getGoal(account, 'mer');
            const roasGoal = getGoal(account, 'roas');
            const aovGoal = getGoal(account, 'aov');

            const cppGoal = orZero(((revenueGoal)) / (merGoal) / ((revenueGoal) / (aovGoal)));
            const adspendGoal = orZero((revenueGoal / merGoal));
            const purchasesGoal = orZero((revenueGoal / aovGoal));
            account.metadata.goals['cpp'] = cppGoal;
            account.metadata.goals['adspend'] = adspendGoal;
            account.metadata.goals['purchases'] = purchasesGoal;

            let updatedAccount = await props.auth.updateAccount(account);
            if (updatedAccount) {
                //await props.auth.silentAuth();
            } else {
                setError("Error updating account.");
            }
        } catch (e) {
            setError(getInnerError(e));
        }
    };

    return (
        <React.Fragment>

            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Goals</title></Helmet>

            <div className={'goals-page page-layout ' + (error != undefined ? ' has-error' : '')}>
                {
                    (loading || !dataReady) ?
                        renderLoader() :

                        // error
                        error ?
                            renderError(ErrorMessages.defaultErrorMessage(), error, "large")

                            // loaded:
                            : (
                                <div className={'cols flex-wrap ' + tab}>

                                    { // Edit Goals overlay
                                        (tab != 'edit-goals') &&

                                        <div className="col tabs">

                                            <div className={'tab edit-goals ' + (tab == 'edit-goals' ? 'active' : '')}>
                                                <div className="label">Your Store Revenue goal for {currentMonth} is {(revenueGoal) != undefined ? shortFormatCurrency(revenueGoal) : 'recalculating...'}</div>
                                                <Button onClick={() => navigate('/goals/edit')} round filled color="green">Edit</Button>
                                            </div>

                                            {renderTab('Store Revenue', 'revenue', revenueGoal, currentMonthData.revenue, monthProjectedRevenue || 0, { price: true })}

                                            {renderTab('Adspend', 'adspend', adspendGoal, currentMonthData.adspend, monthProjectedAdspend || 0, { price: true })}

                                            {renderTab('MER', 'mer', merGoal, currentMonthData.roas, monthProjectedMer || 0, { price: false })}

                                            {renderTab('AOV', 'aov', aovGoal, currentMonthData.aov, monthProjectedAov || 0, { price: false })}

                                            {renderTab('CPO', 'cpp', cppGoal, currentMonthData.cpp, monthProjectedCpp || 0, { price: false, swapped: true })}

                                        </div>
                                    }

                                    <div className="col section">
                                        {
                                            tab == 'edit-goals' ?
                                                <EditGoals
                                                    auth={props.auth}
                                                    revenueGoal={editingRevenueGoal}
                                                    aovGoal={editingAovGoal}
                                                    merGoal={editingMerGoal}
                                                    roasGoal={editingRoasGoal}
                                                    setRevenueGoal={(v) => { updateGoal('revenue', v); setRevenueGoal(v); }}
                                                    setAovGoal={(v) => { updateGoal('aov', v); setAovGoal(v); }}
                                                    setMerGoal={(v) => { updateGoal('mer', v); setMerGoal(v); }}
                                                    setRoasGoal={(v) => { updateGoal('roas', v); setRoasGoal(v); }}
                                                    onClose={(e) => onCloseGoals()}
                                                    processedData={processedData}
                                                    processedROASData={processedROASData}
                                                    mobile={false}
                                                />

                                                : tab == 'revenue' ?
                                                    <BarGraph
                                                        {...props}
                                                        price
                                                        header={<div className="header">Store<br />Revenue</div>}
                                                        name={"Revenue"}
                                                        key="revenue"
                                                        data={revenueData(revenueGoal, DAYS, daysInMonth)}
                                                        options={revenueOptions(monthRevenueSuggestedMax)}
                                                        target={revenueGoal}
                                                        dailyTarget={revenueGoal / daysInMonth}
                                                        actual={currentMonthData.revenue}
                                                        monthLength={daysInMonth}
                                                        projected={monthProjectedRevenue}
                                                        dataReady={dataReady}
                                                        threshold='0'
                                                        processedData={currentMonthData}
                                                        dataset={[...currentMonthData.revenueData]}
                                                    />

                                                    : tab == 'adspend' ?
                                                        <BarGraph
                                                            price
                                                            header={<div className="header">Ad<br />Spend</div>}
                                                            name={"Adspend"}
                                                            key="adspend"
                                                            data={adspendData(adspendGoal, DAYS, daysInMonth, adspendGoal / daysInMonth)}
                                                            options={adspendOptions(monthAdspendSuggestedMax)}
                                                            target={adspendGoal}
                                                            dailyTarget={adspendGoal / daysInMonth}
                                                            actual={currentMonthData.adspend}
                                                            monthLength={daysInMonth}
                                                            projected={monthProjectedAdspend}
                                                            dataReady={dataReady}
                                                            threshold='0'
                                                            processedData={currentMonthData}
                                                            dataset={[...currentMonthData.adspendData]}
                                                        />

                                                        : tab == 'mer' ?
                                                            <BarGraph
                                                                header={<div className="header">MER</div>}
                                                                name={"MER"}
                                                                key="roas"
                                                                data={roasData(merGoal, DAYS, daysInMonth)}
                                                                options={roasOptions(monthMerSuggestedMax)}
                                                                target={merGoal}
                                                                dailyTarget={merGoal / daysInMonth}
                                                                actual={(currentMonthData.mer)}
                                                                projected={monthProjectedMer}
                                                                monthLength={daysInMonth}
                                                                dataReady={dataReady}
                                                                threshold='0'
                                                                processedData={currentMonthData}
                                                                dataset={[...currentMonthData.merData]}
                                                            />

                                                            : tab == 'aov' ?
                                                                <BarGraph
                                                                    price
                                                                    header={<div className="header">AOV</div>}
                                                                    name={"AOV"}
                                                                    key="aov"
                                                                    data={aovData(aovGoal, DAYS, daysInMonth)}
                                                                    options={aovOptions(monthAovSuggestedMax)}
                                                                    target={aovGoal}
                                                                    dailyTarget={aovGoal / daysInMonth}
                                                                    actual={currentMonthData.aov}
                                                                    projected={monthProjectedAov}
                                                                    monthLength={daysInMonth}
                                                                    dataReady={dataReady}
                                                                    threshold='0'
                                                                    processedData={currentMonthData}
                                                                    dataset={[...currentMonthData.aovData]}
                                                                />

                                                                : tab == 'cpp' ?
                                                                    <BarGraph
                                                                        price
                                                                        header={<div className="header">Cost<br />Per Order</div>}
                                                                        name={"CPO"}
                                                                        key="cpp"
                                                                        data={costPerPurchaseData((currentMonthData.totalAdspend / currentMonthData.totalOrders), DAYS, daysInMonth)}
                                                                        options={costPerPurchaseOptions(monthCppSuggestedMax)}
                                                                        target={cppGoal}
                                                                        dailyTarget={cppGoal / daysInMonth}
                                                                        actual={currentMonthData.cpp}
                                                                        projected={monthProjectedCpp}
                                                                        monthLength={daysInMonth}
                                                                        dataReady={dataReady}
                                                                        threshold='0'
                                                                        swapped
                                                                        processedData={currentMonthData}
                                                                        dataset={[...currentMonthData.cppData]}
                                                                    />

                                                                    : <></>
                                        }

                                    </div>

                                </div>
                            )
                }
            </div>

        </React.Fragment>
    );
};

export default Goals;